// NRW state a maximum display scale for the Welsh flood risk data, and the app
// exceeded it until 10/09/2026. From the DataMapWales layer-group record for
// Flood Risk Assessment Wales, verbatim:
//
//   "If data is displayed in map form to others, appropriate map scales should
//    be used given the indicative nature of the modelling. It should be shown
//    with a maximum zoom scale of 1:5,000 and that base mapping of 1:10,000 is
//    used to give context."
//
// These three sources declared no `maxZoom`, so useLayerController defaulted
// them to DEFAULT_MAX_ZOOM, which is 20. At Welsh latitudes zoom 20 is roughly
// 1:326, sixteen times finer than the stated limit, on data NRW describe as
// indicative and explicitly "not specific to individual properties".
//
// WHY 16, derived rather than picked. Web Mercator resolution is
// 156543.03392 * cos(latitude) / 2^zoom metres per pixel, and the OGC scale
// denominator assumes a 0.28 mm pixel, so scale = resolution / 0.00028. Across
// Wales's full latitude range, 51.4 to 53.4:
//
//   zoom 16  ->  1:5,086 to 1:5,322   coarser than the limit, so compliant
//   zoom 17  ->  1:2,543 to 1:2,661   finer than the limit, so not
//
// The comparison in useLayerController is `zoom <= maxZoom`, so 16 renders and
// 17 does not. flood.test.js asserts this from the arithmetic rather than from
// the number, so the cap cannot drift away from the licence condition.
//
// This is a licence-adjacent use limitation and also plain honesty about the
// data: NRW's own note is that the modelling is indicative.
const WALES_FLOOD_MAX_ZOOM = 16;

export const FLOOD_SOURCES = [
  {
    // England rivers & sea — RISK map (NaFRA2 / Risk of Flooding from Rivers and
    // Sea), graded High/Medium/Low/Very Low. Replaces the planning Flood Zones
    // product so the whole flood category reads on one consistent risk scale.
    // The server caps rendering at ~1:50,000 (MaxScaleDenominator 50000) and
    // returns blank tiles when zoomed further out, so minZoom 14 — the layer
    // controller auto-snaps the map to this zoom when flood is toggled on.
    id: 'england-flood-risk-rivers-sea',
    region: 'england',
    kind: 'wms',
    wmsBase:
      'https://environment.data.gov.uk/spatialdata/nafra2-risk-of-flooding-from-rivers-and-sea/wms',
    layerName: 'rofrs_4band',
    minZoom: 14,
    licenceStatus: 'verified',
    precedence: 100,
  },
  {
    // England surface water — already a RISK product (RoFSW), High/Medium/Low.
    id: 'ea-surface-water-england',
    region: 'england',
    kind: 'wms',
    wmsBase:
      'https://environment.data.gov.uk/geoservices/datasets/b5aaa28d-6eb9-460e-8d6f-43caa71fbe0e/wms',
    layerName: 'rofsw',
    minZoom: 8,
    licenceStatus: 'verified',
    unverified: false,
    precedence: 200,
  },
  {
    // Wales rivers — RISK map (Flood Risk Assessment Wales), High/Medium/Low.
    // Replaces the planning NRW_FLOODZONE_RIVERS_SEAS_MERGED layer.
    id: 'wales-flood-risk-rivers',
    region: 'wales',
    kind: 'wms',
    // NRW's stated 1:5,000 maximum display scale. See the note at the top.
    maxZoom: WALES_FLOOD_MAX_ZOOM,
    wmsBase: 'https://datamap.gov.wales/geoserver/inspire-nrw/wms',
    layerName: 'inspire-nrw:NRW_FLOOD_RISK_FROM_RIVERS',
    minZoom: 8,
    licenceStatus: 'verified',
    unverified: false,
    precedence: 100,
  },
  {
    // Wales sea — RISK map (Flood Risk Assessment Wales), High/Medium/Low.
    id: 'wales-flood-risk-sea',
    region: 'wales',
    kind: 'wms',
    // NRW's stated 1:5,000 maximum display scale. See the note at the top.
    maxZoom: WALES_FLOOD_MAX_ZOOM,
    wmsBase: 'https://datamap.gov.wales/geoserver/inspire-nrw/wms',
    layerName: 'inspire-nrw:NRW_FLOOD_RISK_FROM_SEA',
    minZoom: 8,
    licenceStatus: 'verified',
    unverified: false,
    precedence: 101,
  },
  {
    // Wales surface water — RISK map (Flood Risk Assessment Wales), High/Medium/Low.
    // Replaces the blank/broken NRW_FLOODZONE_SURFACE_WATER layer (empty upstream)
    // with the working _FLOOD_RISK_FROM_ equivalent.
    id: 'wales-flood-risk-surface-water',
    region: 'wales',
    kind: 'wms',
    // NRW's stated 1:5,000 maximum display scale. See the note at the top.
    maxZoom: WALES_FLOOD_MAX_ZOOM,
    wmsBase: 'https://datamap.gov.wales/geoserver/inspire-nrw/wms',
    layerName: 'inspire-nrw:NRW_FLOOD_RISK_FROM_SURFACE_WATER_SMALL_WATERCOURSES',
    minZoom: 8,
    licenceStatus: 'verified',
    unverified: false,
    precedence: 200,
  },
  {
    // Scotland — SEPA medium-likelihood extents (unchanged; already reads clearly).
    id: 'sepa-flood-rivers-sea-scotland',
    region: 'scotland',
    kind: 'wms',
    wmsBase:
      'https://map.sepa.org.uk/server/services/Open/Flood_Maps/MapServer/WMSServer',
    layerName:
      'River_Flooding_Medium_Likelihood22646,Coastal_Flooding_Medium_Likelihood21859',
    tileSrs: 'CRS:84',
    minZoom: 8,
    licenceStatus: 'verified',
    unverified: false,
    precedence: 100,
  },
  {
    id: 'sepa-surface-water-scotland',
    region: 'scotland',
    kind: 'wms',
    wmsBase:
      'https://map.sepa.org.uk/server/services/Open/Flood_Maps/MapServer/WMSServer',
    layerName: 'Surface_Water_and_Small_Watercourses_Flooding_Medium_Likelihood29035',
    tileSrs: 'CRS:84',
    minZoom: 8,
    licenceStatus: 'verified',
    unverified: false,
    precedence: 200,
  },
];
